'use client';

import {useState} from 'react';
import {Check} from 'lucide-react';
import CopyIcon from '@/icons/copy.svg';
import {trpc} from '@features/utils/trpc';
import amplitudeService from '@features/analytics/amplitudeService';
import {cn} from '@features/ui/utils';
import {openIntercomArticle} from '@features/utils/intercom';

type Tool = 'claude-code' | 'codex' | 'cursor' | 'antigravity' | 'other';

const tools: {id: Tool; label: string}[] = [
  {id: 'claude-code', label: 'Claude Code'},
  {id: 'codex', label: 'Codex'},
  {id: 'cursor', label: 'Cursor'},
  {id: 'antigravity', label: 'Antigravity'},
  {id: 'other', label: 'Other'},
];

function getConfigForTool(tool: Tool, token: string, origin: string): string {
  const url = `${origin}/api/mcp/mcp`;

  switch (tool) {
    case 'claude-code':
      return `claude mcp add --transport http banani ${url} --header "Authorization: Bearer ${token}"`;
    case 'cursor':
      return JSON.stringify(
        {
          mcpServers: {
            banani: {
              type: 'streamableHttp',
              url,
              headers: {Authorization: `Bearer ${token}`},
            },
          },
        },
        null,
        2,
      );
    case 'codex':
      return `[mcp_servers.banani]\nurl = "${url}"\nhttp_headers = { "Authorization" = "Bearer ${token}" }`;
    case 'antigravity':
      return JSON.stringify(
        {
          mcpServers: {
            banani: {
              serverUrl: url,
              headers: {Authorization: `Bearer ${token}`},
            },
          },
        },
        null,
        2,
      );
    case 'other':
      return '';
  }
}

function getConfigLabel(tool: Tool): string {
  switch (tool) {
    case 'claude-code':
      return 'Run this command in your terminal:';
    case 'cursor':
      return 'Add this to ~/.cursor/mcp.json:';
    case 'codex':
      return 'Add this to ~/.codex/config.toml:';
    case 'antigravity':
      return 'Add this to ~/.gemini/antigravity/mcp_config.json:';
    case 'other':
      return "Use this server URL and API key in your MCP client's settings:";
  }
}

function CopyButton({
  text,
  variant = 'dark',
}: {
  text: string;
  variant?: 'dark' | 'light';
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    amplitudeService.logEvent('[MCP] Command Copied');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={cn(
        'shrink-0 rounded p-1',
        variant === 'dark'
          ? 'text-white/60 hover:text-white'
          : 'text-black/40 hover:text-black/60',
      )}
    >
      {copied ? (
        <div className='flex h-6 w-6 items-center justify-center'>
          <Check size={20} className='mt-0.5 text-green-500' />
        </div>
      ) : (
        <CopyIcon />
      )}
    </button>
  );
}

function HowItWorks() {
  const steps = [
    'Add Banani MCP',
    'Select screens in your Banani project',
    'Ask your agent to work with them',
  ];

  const prompts = [
    'Recreate selected Banani screens in my stack',
    'Make a new React project using these designs',
  ];

  return (
    <div className='flex flex-col'>
      <h2 className='mb-3 text-sm font-semibold'>How it works</h2>
      <div className='flex flex-col gap-2'>
        {steps.map((step, i) => (
          <p key={i} className='text-sm text-black/60'>
            {i + 1}. {step}
          </p>
        ))}
      </div>
      <p className='mb-2 mt-4 text-sm text-black/60'>For example:</p>
      <div className='flex flex-col gap-2'>
        {prompts.map((prompt) => (
          <div
            key={prompt}
            className='flex h-10 items-center justify-between rounded-xl bg-[#E9E9E9] px-3'
          >
            <span className='text-sm text-black/60'>{prompt}</span>
            <CopyButton text={prompt} variant='light' />
          </div>
        ))}
      </div>
      <button
        onClick={() => openIntercomArticle(13793261)}
        className='mt-3 self-start text-sm font-semibold text-primary hover:underline'
      >
        Learn more
      </button>
    </div>
  );
}

function TerminalBlock({
  label,
  content,
  blurred,
  onClick,
  overlayText,
}: {
  label: string;
  content: string;
  blurred?: boolean;
  onClick?: () => void;
  overlayText?: string;
}) {
  return (
    <div>
      <p className='mb-3 text-sm text-black/60'>{label}</p>
      <div
        className={cn(
          'relative flex h-10 rounded-xl bg-[#707070]',
          onClick && 'cursor-pointer',
        )}
        onClick={onClick}
      >
        <div
          className={cn(
            'flex items-center px-3',
            blurred ? '' : 'flex-1 overflow-x-auto pr-10',
          )}
        >
          <code
            className={cn(
              'whitespace-nowrap font-mono text-sm text-white',
              blurred && 'blur-[6px]',
            )}
          >
            {content}
          </code>
        </div>
        {overlayText ? (
          <div className='absolute inset-0 flex items-center justify-center'>
            <span className='text-sm font-semibold text-white'>
              {overlayText}
            </span>
          </div>
        ) : (
          <div className='absolute right-0 top-1 rounded-r-xl bg-gradient-to-l from-[#707070] from-60% to-transparent pl-6 pr-2'>
            <CopyButton text={content} />
          </div>
        )}
      </div>
    </div>
  );
}

function CodeBlock({label, content}: {label: string; content: string}) {
  return (
    <div>
      <p className='mb-3 text-sm text-black/60'>{label}</p>
      <div className='relative rounded-xl bg-[#707070]'>
        <div className='overflow-x-auto px-3 py-2 pr-10'>
          <pre className='font-mono text-sm leading-relaxed text-white'>
            {content}
          </pre>
        </div>
        <div className='absolute right-0 top-1 rounded-r-xl bg-gradient-to-l from-[#707070] from-60% to-transparent pl-6 pr-2'>
          <CopyButton text={content} />
        </div>
      </div>
    </div>
  );
}

function NewConnection() {
  const [selectedTool, setSelectedTool] = useState<Tool>('claude-code');
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const utils = trpc.useUtils();

  const createMutation = trpc.apiTokens.createApiToken.useMutation({
    onSuccess: (data) => {
      setRevealedToken(data.rawToken);
      void utils.apiTokens.getApiToken.invalidate();
    },
  });

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const config = revealedToken
    ? getConfigForTool(selectedTool, revealedToken, origin)
    : '';
  const label = getConfigLabel(selectedTool);
  const serverUrl = `${origin}/api/mcp/mcp`;

  const handleReveal = () => {
    amplitudeService.logEvent('[MCP] Token Generated');
    createMutation.mutate({name: `Key ${Date.now()}`});
  };

  return (
    <div className='space-y-4'>
      <h2 className='text-sm font-semibold'>New MCP connection</h2>

      <div className='flex h-10 w-full gap-1 rounded-xl bg-[#E9E9E9] p-1'>
        {tools.map(({id, label: toolLabel}) => (
          <button
            key={id}
            onClick={() => {
              setSelectedTool(id);
              amplitudeService.logEvent('[MCP] Tool Tab Selected', {tool: id});
            }}
            className={cn(
              'flex-1 rounded-lg text-sm font-semibold transition-all',
              selectedTool === id
                ? 'bg-white text-foreground shadow-sm'
                : 'text-black/50 hover:text-foreground',
            )}
          >
            {toolLabel}
          </button>
        ))}
      </div>

      {revealedToken ? (
        <div className='space-y-4'>
          {selectedTool === 'other' ? (
            <div className='space-y-4'>
              <TerminalBlock label='Server URL' content={serverUrl} />
              <TerminalBlock label='API Key' content={revealedToken} />
            </div>
          ) : selectedTool === 'claude-code' ? (
            <TerminalBlock label={label} content={config} />
          ) : (
            <CodeBlock label={label} content={config} />
          )}

          <p className='text-xs text-black/60'>
            This token will only be shown once. If you lose it, you&apos;ll need
            to delete this key and create a new one.
          </p>
        </div>
      ) : (
        <TerminalBlock
          label={getConfigLabel(selectedTool)}
          content={getConfigForTool(selectedTool, 'bnni_xxxxxxxxxxxx', origin)}
          blurred
          onClick={!createMutation.isPending ? handleReveal : undefined}
          overlayText={
            createMutation.isPending ? 'Generating...' : 'Click to reveal'
          }
        />
      )}
    </div>
  );
}

function YourKeys() {
  const utils = trpc.useUtils();
  const tokensQuery = trpc.apiTokens.getApiToken.useQuery();
  const tokens = tokensQuery.data ?? [];

  const revokeMutation = trpc.apiTokens.revokeApiToken.useMutation({
    onSuccess: () => {
      void utils.apiTokens.getApiToken.invalidate();
    },
  });

  const handleRevoke = (id: string) => {
    amplitudeService.logEvent('[MCP] Token Revoked');
    revokeMutation.mutate({id});
  };

  if (tokens.length === 0) return null;

  return (
    <>
      <div className='h-px bg-black/10' />
      <div className='space-y-3'>
        <h2 className='mb-4 text-sm font-semibold'>Your keys</h2>
        <div className='flex flex-col gap-4'>
          {tokens.map((token) => (
            <div key={token.id} className='flex items-center justify-between'>
              <span className='font-mono text-sm text-black/60'>
                {token.tokenPrefix}****************
              </span>
              <button
                onClick={() => handleRevoke(token.id)}
                disabled={revokeMutation.isPending}
                className='cursor-pointer text-sm font-semibold text-black/60 hover:text-foreground disabled:opacity-50'
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export function McpContent() {
  return (
    <div className='mb-2 space-y-8'>
      <HowItWorks />
      <div className='h-px bg-black/10' />
      <NewConnection />
      <YourKeys />
    </div>
  );
}

export function McpView() {
  return (
    <div className='mb-10 flex flex-1 justify-center'>
      <div className='w-full max-w-xl pt-20'>
        <h1 className='mb-6 text-2xl font-semibold text-gray-900'>MCP</h1>
        <div className='mb-6 border-b border-gray-200' />
        <McpContent />
      </div>
    </div>
  );
}
